import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type DragEvent as RDragEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { useStore } from '../store';
import { conn as connection } from '../net/connection';
import { machineName, store, toast } from '../store';
import { desktopPaste, framePressedAt, machineClipboard, setDesktopFrame } from './desktopLink';
import { copyText } from './sheets';
import { getTerm, rememberDims } from '../term/manager';
import type { SessionTerm } from '../term/SessionTerm';
import { COARSE_MQ, keyboardOpen, useMedia } from './mobile';
import { useActions } from './Workspace';
import { LAYOUT_SETTLED, layoutMoving } from './motion';
import { current, groupOf, isBlank, MAX_PANES, rowSessions, type Pane } from './tabs';
import type { Session } from '../net/protocol';
import { numberedSessions, orderedSessions } from './organize';
import { isMac, numModLabel } from './keys';
import { agoPhrase, secondsSince, useNow } from './time';
import { desktopProblem } from './desktopProblem';
import { Button, Carets, Empty, EmptyArt, Hint, Ic, Meta, Mark, Pix, Tool } from './ds';
import { PaneBar, statusMark } from './PaneBar';
import { IconAlertTriangle, IconAspectRatio, IconClipboard, IconExternalLink, IconHistory, IconTerminal, IconEye, IconHandClick, IconHandStop, IconMaximize, IconPlayerPlay, IconPower, IconZoomReset } from '@tabler/icons-react';
import { LoginOverlay } from './LoginOverlay';
import { PaneToasts } from './PaneToasts';
import { LowBandwidth, streamRate } from './StreamMode';
import { DesktopSettings } from './DesktopSettings';
import { Dropdown } from './Dropdown';
import { DesktopDock } from './DesktopDock';
import { filesFromDrop, sendFiles } from '../term/drops';
import { VoiceCard } from './VoiceCard';
import { UploadBanner } from './UploadBanner';
import { ZoomReadout } from './ZoomReadout';

export function PaneView({ index, pane, focused, multi }: { index: number; pane: Pane | null; focused: boolean; multi: boolean }) {
  const a = useActions();
  const tab = pane?.tabs[pane.active];
  // a blank tab (+) offers what to show, as an empty pane does
  const blank = tab !== undefined && isBlank(tab) ? tab : undefined;
  const sid = blank === undefined ? tab : undefined;
  return (
    <section
      className="pane ws-pane"
      data-focused={focused && multi ? '' : undefined}
      data-testid="pane"
      data-pane={index}
      data-kind={sid !== undefined ? 'session' : 'empty'}
      data-session-id={sid}
      onPointerDownCapture={() => a.focusPane(index)}
      onFocusCapture={() => a.focusPane(index)}
    >
      {!a.compact && pane && <PaneBar index={index} pane={pane} focused={focused} multi={multi} />}
      {sid !== undefined ? <TerminalPane key={sid} sid={sid} /> : <EmptyPane multi={multi} index={index} blank={blank} />}
      {focused && multi && <span className="ws-pane-ring" aria-hidden="true" />}
    </section>
  );
}


/* No session open: the board's "Start something" column, centred. In a split, an empty pane lists what it could
   show instead (open tabs no pane shows, then sessions that have no tab) and ends on New terminal. A blank tab (+)
   shows the same, and what's picked takes the tab's place */
type PickRow = { key: string; lead: ReactNode; name: string; meta?: string; hint?: number; run: () => void };

function PickButton({ r, size, first, testid }: { r: PickRow; size: 'lg' | 'sm'; first?: boolean; testid?: string }) {
  return (
    <button type="button" className="ws-pick-row" data-size={size} data-first={first ? '' : undefined} data-testid={testid} onClick={r.run}>
      <span className="ws-pick-lead">{r.lead}</span>
      <span className="ws-pick-text">
        <span className="ws-pick-name">{r.name}</span>
        {r.meta && <Meta>{r.meta}</Meta>}
      </span>
      {r.hint !== undefined &&
        (isMac() ? (
          <Hint k={String(r.hint)} mod />
        ) : (
          <span className="ds-hint">
            {numModLabel()}
            {r.hint}
          </span>
        ))}
    </button>
  );
}

/** Arrow keys walk a picker's rows. */
const walkRows = (e: KeyboardEvent<HTMLElement>) => {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const rows = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('.ws-pick-row'));
  const at = rows.indexOf(document.activeElement as HTMLButtonElement);
  const next = rows[Math.min(rows.length - 1, Math.max(0, at + (e.key === 'ArrowDown' ? 1 : -1)))];
  if (!next) return;
  e.preventDefault();
  next.focus();
};

function EmptyPane({ multi, index, blank }: { multi: boolean; index: number; blank?: number }) {
  const a = useActions();
  const now = useNow();
  const loaded = useStore((s) => s.sessionsLoaded);
  const conn = useStore((s) => s.conn);
  const host = useStore((s) => s.me?.machine?.id);
  const sessions = useStore((s) => s.sessions);
  const closed = useStore((s) => s.closed)[0];
  if (!loaded || !a.ready) {
    return (
      <div className="pane-empty">
        <div className="loading-label">{conn !== 'connected' ? 'connecting' : loaded ? 'loading workspace' : 'loading sessions'}</div>
      </div>
    );
  }
  const newTerminal: PickRow = { key: 'new', lead: <Ic icon={IconTerminal} />, name: 'New terminal', meta: 'A shell in ~', run: () => void a.newSession(multi || blank !== undefined ? { pane: index, blank } : undefined) };

  if (!multi) {
    const last = closed ? [closed.row?.meta.none.program ?? closed.program, closed.row?.meta.none.where ?? closed.cwd, agoPhrase(secondsSince(closed.closedAt, now))] : [];
    const reopen: PickRow | null = closed ? { key: 'reopen', lead: <Ic icon={IconHistory} />, name: 'Reopen last session', meta: last.filter(Boolean).join(' · '), run: () => void a.reopen(closed.id, { pane: index, blank }) } : null;
    return (
      <div className="pane-empty" data-testid="empty-pane">
        <div className="ws-start" onKeyDown={walkRows}>
          <Empty
            size="lg"
            art="machine"
            className="ws-start-head"
            kicker={host ? `${host} · idle` : 'idle'}
            title={host ? `Start something on ${host}.` : 'Start something.'}
            titleAs="h1"
            lead="It keeps running when you close the lid."
          />
          <div className="ws-pick-list">
            <PickButton r={newTerminal} size="lg" testid="empty-new-session" />
            {reopen && <PickButton r={reopen} size="lg" />}
          </div>
        </div>
      </div>
    );
  }

  // ⌘1–9 count the sidebar's rows
  const order = numberedSessions(sessions, a.tabs).map((s) => s.id);
  // four panes: the board drops the heading and tightens the rows
  const dense = a.paneCount >= MAX_PANES;
  const sub = (s: Session) => [s.row?.meta.none.program, s.row?.meta.none.where].filter(Boolean).join(' · ');
  // the rows that are one session (picked, it moves into this pane and its row goes), the three used last; the rest
  // are a ⌘K away. A row with tabs or panes of its own stays a row.
  const here = current(a.tabs);
  const lone = (id: number) => groupOf(a.tabs, id) !== here && rowSessions(a.tabs, id).length === 1;
  const usedAt = (s: Session) => Date.parse(s.lastActivity ?? '') || 0;
  const sessionRows: PickRow[] = orderedSessions(sessions, a.tabs)
    .filter((s) => lone(s.id))
    .sort((x, y) => usedAt(y) - usedAt(x))
    .slice(0, 3)
    .map((s) => ({
      key: `s${s.id}`,
      lead: <Mark {...statusMark(s.status)} />,
      name: s.name,
      meta: sub(s),
      hint: order.indexOf(s.id) >= 0 && order.indexOf(s.id) < 9 ? order.indexOf(s.id) + 1 : undefined,
      run: () => a.fillPane(index, s.id, blank),
    }));
  return (
    <div className="pane-empty" data-testid="empty-pane" data-dense={dense ? '' : undefined}>
      <div className="ws-pick" onKeyDown={walkRows}>
        {!dense && <h2 className="ws-pick-title">Pick up where you left off</h2>}
        {sessionRows.length > 0 && (
          <>
            <div className="ws-pick-label">
              <Pix>Recent</Pix>
            </div>
            {sessionRows.map((r) => (
              <PickButton key={r.key} r={r} size="sm" />
            ))}
            <div className="ws-pick-rule" />
          </>
        )}
        <PickButton r={newTerminal} size="sm" testid="empty-new-session" />
      </div>
    </div>
  );
}

/**
 * Touch devices: a vertical drag scrolls the terminal (xterm 6 has no touch scrolling of its own),
 * with a little momentum; a tap focuses the terminal and opens the on-screen keyboard.
 */
/** A short ring on the cell a tap clicked, so it's clear the click happened and where. */
function tapRing(host: HTMLElement, t: SessionTerm, clientX: number, clientY: number) {
  const screen = host.querySelector<HTMLElement>('.xterm-screen');
  if (!screen) return;
  const r = screen.getBoundingClientRect();
  const cw = r.width / t.term.cols;
  const rh = r.height / t.term.rows;
  const col = Math.min(t.term.cols - 1, Math.max(0, Math.floor((clientX - r.left) / cw)));
  const row = Math.min(t.term.rows - 1, Math.max(0, Math.floor((clientY - r.top) / rh)));
  const h = host.getBoundingClientRect();
  const ring = document.createElement('div');
  ring.className = 'tap-ring';
  ring.dataset.testid = 'tap-ring';
  ring.dataset.cell = `${col + 1},${row + 1}`; // 1-based, as the app sees it
  ring.style.left = `${r.left - h.left + (col + 0.5) * cw}px`;
  ring.style.top = `${r.top - h.top + (row + 0.5) * rh}px`;
  host.appendChild(ring);
  ring.addEventListener('animationend', () => ring.remove(), { once: true });
  setTimeout(() => ring.remove(), 1000); // reduced motion: no animationend
}

function useTouchTerminal(ref: RefObject<HTMLDivElement | null>, t: SessionTerm, enabled: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    let id = -1;
    let mode: 'idle' | 'pending' | 'scroll' | 'other' = 'idle';
    let startX = 0;
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let x = 0;
    let y = 0;
    let acc = 0;
    let v = 0; // px/ms, positive = finger moving down
    let raf = 0;

    const stopFling = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };
    // Finger down by dy px → content follows the finger (older lines come into view).
    const step = (dy: number) => {
      acc += dy;
      const h = t.rowHeight;
      const lines = Math.trunc(acc / h);
      if (lines) {
        acc -= lines * h;
        t.touchScroll(-lines, x, y);
      }
    };
    const onStart = (e: TouchEvent) => {
      stopFling();
      if (e.touches.length !== 1) {
        mode = 'other';
        return;
      }
      const p = e.touches[0];
      id = p.identifier;
      startX = x = p.clientX;
      startY = lastY = y = p.clientY;
      lastT = e.timeStamp;
      acc = 0;
      v = 0;
      mode = 'pending';
    };
    const onMove = (e: TouchEvent) => {
      if (mode === 'idle' || mode === 'other') return;
      const p = Array.from(e.touches).find((q) => q.identifier === id);
      if (!p) return;
      if (mode === 'pending') {
        const dx = p.clientX - startX;
        const dy = p.clientY - startY;
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        mode = Math.abs(dy) >= Math.abs(dx) ? 'scroll' : 'other';
        if (mode !== 'scroll') return;
      }
      if (e.cancelable) e.preventDefault();
      const dy = p.clientY - lastY;
      const dt = Math.max(1, e.timeStamp - lastT);
      v = 0.7 * (dy / dt) + 0.3 * v;
      lastY = y = p.clientY;
      x = p.clientX;
      lastT = e.timeStamp;
      step(dy);
    };
    const onEnd = (e: TouchEvent) => {
      const flung = mode === 'scroll' && e.timeStamp - lastT < 80 && Math.abs(v) > 0.2 && t.scrollsLocally;
      mode = 'idle';
      if (!flung) return;
      let prev = performance.now();
      const tick = (now: number) => {
        const dt = Math.min(32, now - prev);
        prev = now;
        step(v * dt);
        v *= Math.pow(0.994, dt);
        raf = Math.abs(v) > 0.03 ? requestAnimationFrame(tick) : 0;
      };
      raf = requestAnimationFrame(tick);
    };
    // Tap: focus the terminal. If it already has focus but the keyboard was dismissed
    // (Android back button), blur first so focus() brings the keyboard back. When the app
    // has the mouse (Claude Code fullscreen, vim), xterm also sent it a click at this cell:
    // mark where it landed.
    const onClick = (e: MouseEvent) => {
      if (t.focused && !keyboardOpen()) t.blur();
      t.focus();
      if (t.term.modes.mouseTrackingMode !== 'none') tapRing(el, t, e.clientX, e.clientY);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    el.addEventListener('click', onClick);
    return () => {
      stopFling();
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('click', onClick);
    };
  }, [ref, t, enabled]);
}

function TerminalPane({ sid }: { sid: number }) {
  const a = useActions();
  const hostRef = useRef<HTMLDivElement>(null);
  const coarse = useMedia(COARSE_MQ);
  const session = useStore((s) => s.sessions.find((x) => x.id === sid));
  const machine = useStore((s) => s.me?.machine?.id) || 'the cloud machine';
  const t = getTerm(sid);
  const exitCode = useSyncExternalStore(
    (cb) => t.subscribe(cb),
    () => t.exitCode,
  );
  const code = exitCode ?? (session?.status === 'exited' ? session.exitCode : null);
  const exited = code !== null || session?.status === 'exited';

  useLayoutEffect(() => {
    const host = hostRef.current!;
    t.mount(host);
    let raf = 0;
    const fit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        t.refit();
        rememberDims(t.term.cols, t.term.rows);
      });
    };
    // while the sidebar slides or the panes glide the pane widens or narrows every frame: hold the grid, fit once it settles
    const ro = new ResizeObserver(() => (layoutMoving() ? cancelAnimationFrame(raf) : fit()));
    ro.observe(host);
    window.addEventListener(LAYOUT_SETTLED, fit);
    return () => {
      ro.disconnect();
      window.removeEventListener(LAYOUT_SETTLED, fit);
      cancelAnimationFrame(raf);
      t.unmount(host);
    };
  }, [t]);

  useTouchTerminal(hostRef, t, coarse);

  // Files dropped on the terminal are uploaded to the machine and their paths pasted there.
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const hasFiles = (e: RDragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const dropProps = exited
    ? {}
    : {
        onDragEnter: (e: RDragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          dragDepth.current++;
          setDragging(true);
        },
        onDragOver: (e: RDragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        },
        onDragLeave: (e: RDragEvent) => {
          if (!hasFiles(e)) return;
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragging(false);
        },
        onDrop: (e: RDragEvent) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          dragDepth.current = 0;
          setDragging(false);
          const { files, folders } = filesFromDrop(e.dataTransfer);
          sendFiles(t, files, { folders });
          t.focus();
        },
      };

  return (
    <>
      <div className={`term-wrap ${dragging ? 'is-drop' : ''}`} {...dropProps} data-testid="term-wrap">
        <div className="term-slot" ref={hostRef} data-testid="terminal" />
        {dragging && (
          <div className="drop-hint" aria-hidden="true">
            Drop to upload to {machine}
          </div>
        )}
        <PaneToasts sid={sid} />
        <UploadBanner sid={sid} />
        <ZoomReadout sid={sid} />
        <VoiceCard sid={sid} />
        {exited && session && (
          <div className="exit-bar" role="status" data-testid="exited">
            <span>
              Process exited with code <b data-testid="exit-code">{code ?? '?'}</b>
            </span>
            <button className="btn btn-xs btn-primary" data-testid="restart" onClick={() => void a.restart(session)}>
              Restart
            </button>
            <button className="btn btn-xs" onClick={() => void a.kill(session.id)}>
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function useElementSize(ref: RefObject<HTMLElement | null>) {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

// Desktop screen sizes. "auto" follows the pane; the others are fixed (agents see a stable
// screen). The choice is per machine and reaches the stream client through the agent's bridge.
// On phones Auto (the default there) is the pane's shape at 1.5 CSS px per desktop px, like
// the iOS app's "Balanced": the whole desktop fits, top panel included, with nothing to pan.
const DESKTOP_RES = [
  { value: 'auto', label: 'Auto' },
  { value: '1280x720', label: '720p' },
  { value: '1600x900', label: '900p' },
  { value: '1920x1080', label: '1080p' },
  { value: '2560x1440', label: '1440p' },
] as const;
const DEFAULT_RES = '1920x1080';
const PHONE_DENSITY = 1.5;
const resKey = (machine?: string) => `cmx.desktopRes.${machine ?? 'default'}`;

/** The saved choice for this machine, or null when there's none. */
function loadRes(machine?: string): string | null {
  try {
    const v = localStorage.getItem(resKey(machine));
    if (v && DESKTOP_RES.some((r) => r.value === v)) return v;
  } catch {
    /* storage unavailable */
  }
  return null;
}

const ACTUAL_KEY = 'cmx.desktopActual';
function loadFlag(key: string, machine?: string): boolean {
  try {
    return localStorage.getItem(`${key}.${machine ?? 'default'}`) === '1';
  } catch {
    return false;
  }
}
function saveFlag(key: string, machine: string | undefined, on: boolean) {
  try {
    localStorage.setItem(`${key}.${machine ?? 'default'}`, on ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
}

/** Phones' Auto: the pane's shape at PHONE_DENSITY, in even sizes the stream bridge takes. */
function phoneFit(size: { w: number; h: number }): string {
  const px = (v: number) => Math.max(320, Math.min(3840, Math.floor((v * PHONE_DENSITY) / 2) * 2));
  return `${px(size.w)}x${px(size.h)}`;
}

/**
 * This tab's id for desktop control (one controller; the other tabs and devices view). A
 * reload keeps it, so a controlling tab stays in control across reloads: it is taken out of
 * sessionStorage on load (a duplicated tab then gets its own) and put back on pagehide.
 */
const TAB_ID = (() => {
  const KEY = 'cmx.desktopTab';
  let id = '';
  try {
    id = sessionStorage.getItem(KEY) ?? '';
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
  if (!id) id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  window.addEventListener('pagehide', () => {
    try {
      sessionStorage.setItem(KEY, id);
    } catch {
      /* storage unavailable */
    }
  });
  return id;
})();
const HEARTBEAT_MS = 5000;

/**
 * Who drives the desktop stream: Selkies allows one controller and a newer one silently kicks
 * the older off. The agent keeps the controller; this tab claims it when free, heartbeats while
 * it has it, drops to view-only when someone takes control, and takes over again when it's free.
 */
function useDesktopRole(running: boolean, connected: boolean) {
  const controller = useStore((s) => s.desktopController);
  const [role, setRole] = useState<'control' | 'view' | null>(null);
  const roleRef = useRef(role);
  roleRef.current = role;
  const active = running && connected;

  const claim = async (force = false) => {
    try {
      const r = await connection.control<{ controller: string; granted: boolean }>('desktop.claim', { client: TAB_ID, force });
      setRole(r.granted ? 'control' : 'view');
    } catch {
      /* connection dropped: the next (re)connect claims again */
    }
  };

  useEffect(() => {
    if (!active) {
      setRole(null);
      return;
    }
    void claim();
    const t = setInterval(() => {
      if (roleRef.current === 'control') void claim();
    }, HEARTBEAT_MS);
    const release = () => void connection.control('desktop.release', { client: TAB_ID }).catch(() => {});
    window.addEventListener('pagehide', release);
    return () => {
      clearInterval(t);
      window.removeEventListener('pagehide', release);
      if (roleRef.current === 'control') release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    if (!active || role === null) return;
    if (role === 'control' && controller && controller !== TAB_ID) setRole('view');
    else if (role === 'view' && controller === '') void claim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, role, active]);

  return { role, takeControl: () => void claim(true) };
}

/**
 * Laptop clipboard sync needs the browser's clipboard permission on this site (delegated to
 * the stream frame). Chromium can report it; elsewhere the chip stays hidden.
 */
function useClipboardPermission(enabled: boolean) {
  const [state, setState] = useState<PermissionState | 'unsupported'>('unsupported');
  useEffect(() => {
    if (!enabled || !navigator.permissions?.query) return;
    let status: PermissionStatus | null = null;
    const update = () => status && setState(status.state);
    navigator.permissions
      .query({ name: 'clipboard-read' as PermissionName })
      .then((s) => {
        status = s;
        update();
        s.addEventListener('change', update);
      })
      .catch(() => setState('unsupported'));
    return () => status?.removeEventListener('change', update);
  }, [enabled]);
  const ask = () => {
    void navigator.clipboard.readText().then(
      () => setState('granted'),
      () => void navigator.permissions.query({ name: 'clipboard-read' as PermissionName }).then((s) => setState(s.state)).catch(() => {}),
    );
  };
  return { state: enabled ? state : 'unsupported', ask };
}

/** The stream client's own focusing happens within this long of its frame loading. */
const FRAME_SETTLE_MS = 8000;

/**
 * The stream client focuses itself when it loads, which would take the keyboard from the
 * terminal someone is typing in (the desktop is often shown beside it by itself). Focus that
 * moves into the frame is the person's when they just pressed in it (its bridge says so), or
 * (a client without that) when the pointer is over it once it has settled. Otherwise: give it back.
 */
function useFocusGuard(frameRef: RefObject<HTMLIFrameElement | null>) {
  useEffect(() => {
    let hovered = false;
    let loadedAt = 0;
    let last: HTMLElement | null = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = () => frameRef.current;
    const enter = (e: Event) => e.target === frame() && (hovered = true);
    const leave = (e: Event) => e.target === frame() && (hovered = false);
    const load = (e: Event) => e.target === frame() && (loadedAt = Date.now());
    const focusin = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target !== frame()) last = e.target;
    };
    // Late enough for the bridge's word on a press to have come.
    const blur = () =>
      setTimeout(() => {
        const f = frame();
        if (!f || document.activeElement !== f || !last?.isConnected) return;
        const now = Date.now();
        const person = now - framePressedAt() < 1000 || (hovered && now - loadedAt > FRAME_SETTLE_MS);
        if (!person) last.focus({ preventScroll: true });
      }, 150);
    document.addEventListener('pointerover', enter, true);
    document.addEventListener('pointerout', leave, true);
    document.addEventListener('load', load, true);
    document.addEventListener('focusin', focusin);
    window.addEventListener('blur', blur);
    return () => {
      document.removeEventListener('pointerover', enter, true);
      document.removeEventListener('pointerout', leave, true);
      document.removeEventListener('load', load, true);
      document.removeEventListener('focusin', focusin);
      window.removeEventListener('blur', blur);
    };
  }, [frameRef]);
}

export function DesktopPane() {
  const a = useActions();
  const running = useStore((s) => s.desktopRunning);
  const busy = useStore((s) => s.desktopBusy);
  const error = useStore((s) => s.desktopError);
  const conn = useStore((s) => s.conn);
  const machineId = useStore((s) => s.me?.machine?.id);
  const net = useStore((s) => s.desktopNet);
  const rtt = useStore((s) => s.rtt);
  const [loaded, setLoaded] = useState(false);
  const [gen, setGen] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const size = useElementSize(stageRef);
  const { role, takeControl } = useDesktopRole(running, conn === 'connected');
  // The frame starts at /preview here and redirects to the desktop's own host; a bare
  // allow="clipboard-read" would only cover the starting origin, so name the final one.
  const me = useStore((s) => s.me);
  const desktopOrigin = me?.machine ? `${me.scheme || location.protocol.replace(':', '')}://desktop-${me.machine.id}${me.previewSuffix}` : '*';
  const frameAllow = ['autoplay', 'clipboard-read', 'clipboard-write', 'fullscreen', 'screen-wake-lock'].map((f) => `${f} ${desktopOrigin}`).join('; ');
  useFocusGuard(frameRef);
  const viewing = role === 'view';
  useEffect(() => {
    store.set({ desktopControlling: role === 'control' });
    return () => store.set({ desktopControlling: false });
  }, [role]);
  const clip = useClipboardPermission(!a.compact && role === 'control');
  const [saved, setSaved] = useState(() => loadRes(machineId));
  useEffect(() => setSaved(loadRes(machineId)), [machineId]);
  const res = saved ?? (a.compact ? 'auto' : DEFAULT_RES);
  // What the stream client is asked for: phones fit Auto to the pane themselves (the client's
  // own Auto is one desktop px per CSS px, too cramped there).
  const fit = a.compact && size ? phoneFit(size) : null;
  const wire = (v: string) => (v === 'auto' && fit ? fit : v);
  // Fit to window scales the desktop's picture to the pane; Actual size shows a fixed resolution
  // pixel for pixel, panned by scrolling the pane. Auto already is the pane's size, so only the
  // fixed resolutions have two ways to show.
  const [actual, setActual] = useState(() => loadFlag(ACTUAL_KEY, machineId));
  const sizeNow = res === 'auto' ? null : res.split('x').map(Number);
  useEffect(() => setActual(loadFlag(ACTUAL_KEY, machineId)), [machineId]);
  const setScale = (v: boolean) => {
    setActual(v);
    saveFlag(ACTUAL_KEY, machineId, v);
  };
  const driver = useStore((s) => s.desktopDriver);
  const [fs, setFs] = useState(false);
  const showActual = actual && !!sizeNow && !fs; // fullscreen always fits the screen
  const [fsRes, setFsRes] = useState('auto'); // fullscreen-only choice; exiting restores `res`
  const [bar, setBar] = useState(false);
  const barTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setLoaded(false);
    if (running) setGen((g) => g + 1);
  }, [running]);
  // The resolution rides in the URL fragment so the client asks for it on its first connect
  // (no flash to the pane size). Fixed per frame load; later changes go by message.
  // Viewers load the client with "#shared" (Selkies' view-only role): it never resizes or
  // takes input, and never kicks the controller off.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const asked = useMemo(() => wire(res), [gen, viewing]);
  const src = `/preview?port=desktop#${viewing ? 'shared&' : ''}cmx-res=${asked}`;

  /** What the stream client was last asked for (by the frame's URL, then by message). */
  const sent = useRef('');
  useEffect(() => {
    sent.current = asked;
  }, [asked, gen, viewing]);
  const post = (value: string) => {
    sent.current = value;
    frameRef.current?.contentWindow?.postMessage({ type: 'cmx-res', value }, '*');
  };
  const changeRes = (value: string) => {
    setSaved(value);
    try {
      localStorage.setItem(resKey(machineId), value);
    } catch {
      /* storage unavailable */
    }
    post(wire(value));
  };

  // Phones: Auto follows the pane's shape (rotation, the dock, a first size the frame loaded
  // without) once it settles. Not while the on-screen keyboard is up: it only covers the
  // desktop for a moment, and the desktop shouldn't jump each time.
  const want = loaded && role === 'control' && res === 'auto' && fit ? fit : null;
  useEffect(() => {
    if (!want || want === sent.current || keyboardOpen()) return;
    const t = setTimeout(() => post(want), sent.current ? 450 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want]);

  // Fullscreen fits the screen: Auto while fullscreen, the saved choice again on exit.
  const resRef = useRef(res);
  resRef.current = res;
  const wireRef = useRef(wire);
  wireRef.current = wire;
  useEffect(() => {
    const onChange = () => {
      const on = !!wrapRef.current && document.fullscreenElement === wrapRef.current;
      setFs(on);
      setFsRes('auto');
      setBar(false);
      post(on ? 'auto' : wireRef.current(resRef.current));
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const showBar = () => {
    if (barTimer.current) clearTimeout(barTimer.current);
    setBar(true);
  };
  const hideBarSoon = () => {
    if (barTimer.current) clearTimeout(barTimer.current);
    barTimer.current = setTimeout(() => setBar(false), 1200);
  };

  const fullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void el.requestFullscreen?.().catch(() => {});
  };

  const resItems = DESKTOP_RES.map((r) => ({
    value: r.value as string,
    // Phones say just Auto: the header has to fit, all of it.
    label: r.value === 'auto' && size && !a.compact ? `${fs ? 'Fit screen' : 'Auto'} · ${size.w} × ${size.h}` : r.label,
  }));
  const picker = (testid: string) => (
    <Dropdown
      value={fs ? fsRes : res}
      items={resItems}
      heading="Resolution"
      label="Desktop resolution"
      title={fit && res === 'auto' ? `Desktop resolution: fits this screen (${fit.replace('x', '×')})` : 'Desktop resolution'}
      testid={testid}
      onChange={(v) => {
        if (fs) {
          setFsRes(v);
          post(v);
        } else changeRes(v);
      }}
    />
  );

  const state = running ? (loaded ? 'live' : 'connecting') : busy ? 'starting' : 'stopped';
  const shownRes = res === 'auto' ? (size ? `${size.w} × ${size.h}` : 'Auto') : res.replace('x', ' × ');
  const about = running
    ? loaded
      ? [shownRes, net?.fps ? streamRate(net) : '', rtt !== null ? `${rtt < 10 ? rtt.toFixed(1) : Math.round(rtt)} ms` : ''].filter(Boolean).join(' · ')
      : 'Connecting'
    : busy
      ? 'Starting'
      : 'Stopped';
  const pasteHere = async () => {
    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch {
      toast({ title: `✗ Couldn’t read this computer’s clipboard`, body: 'Allow clipboard for this site, or press ⌘V in the desktop.', kind: 'error' }, 5000);
      return;
    }
    if (text) desktopPaste(text);
  };

  return (
    <>
      {/* Phones: the top bar already says "Desktop · stopped" and the card has the Start button. */}
      {(running || !a.compact) && (
        <header className="ws-dbar" data-testid="desktop-bar" data-state={state}>
          <span className="ws-dbar-id">
            <Mark tone={running && loaded ? 'live' : running || busy ? 'run' : 'idle'} blink={busy || (running && !loaded)} />
            <span className="ws-dbar-name">Desktop</span>
            {!a.compact && <Meta>{about}</Meta>}
          </span>
          <span className="ws-grow" />
          {running && <LowBandwidth />}
          {running && viewing && (
            <>
              <span className="ws-chip" title="Another tab is in control" data-testid="desktop-viewing">
                <Ic icon={IconEye} size={14} />
                View only
              </span>
              <Button variant="primary" size="sm" onClick={takeControl} data-testid="desktop-take-control">
                <Ic icon={IconHandClick} size={14} />
                Take control
              </Button>
            </>
          )}
          {running && !viewing && a.compact && picker('desktop-res')}
          {running && !viewing && a.compact && (
            <Button
              size="sm"
              data-testid="desktop-copy"
              onClick={() => {
                const t = machineClipboard();
                if (!t) {
                  toast({ title: `Nothing copied on ${machineName()}’s desktop yet`, body: 'Copy something in a desktop app first.' }, 5000);
                  return;
                }
                void copyText(t).then((ok) => toast({ title: ok ? `✓ Copied from ${machineName()}’s desktop` : '✗ Couldn’t copy to this computer’s clipboard', kind: ok ? undefined : 'error' }, 3000));
              }}
            >
              Copy
            </Button>
          )}
          {running ? (
            <>
              {!a.compact && (
                <>
                  <Tool icon={IconAspectRatio} radius={8} label="Fit to window" pressed={!showActual} data-testid="desktop-fit" onClick={() => setScale(false)} />
                  <Tool
                    icon={IconZoomReset}
                    radius={8}
                    label="Actual size"
                    sub={sizeNow ? undefined : 'Pick a fixed resolution in settings'}
                    pressed={showActual}
                    disabled={!sizeNow}
                    data-testid="desktop-actual"
                    onClick={() => setScale(true)}
                  />
                </>
              )}
              <span className="ws-dbar-rule" aria-hidden="true" />
              {!viewing && !a.compact && <Tool icon={IconClipboard} radius={8} label={`Paste to ${machineId ?? machineName()}`} data-testid="desktop-paste" onClick={() => void pasteHere()} />}
              {!viewing && !a.compact && <DesktopSettings clip={clip} res={res} resItems={resItems} onRes={changeRes} />}
              {!a.compact && <Tool icon={IconMaximize} radius={8} label="Full screen" data-testid="desktop-fullscreen" onClick={fullscreen} />}
              {a.compact && <Tool icon={IconExternalLink} radius={8} label="Open in a new tab" data-testid="desktop-newtab" onClick={() => a.openExternal('desktop')} />}
              <Tool icon={IconPower} radius={8} tone="danger" align="end" label={busy ? 'Stopping…' : 'Stop desktop stream'} disabled={busy} data-testid="desktop-stop" onClick={() => void a.stopDesktop()} />
            </>
          ) : (
            <Button variant="primary" size="sm" disabled={busy || conn !== 'connected'} onClick={() => void a.startDesktop()} data-testid="desktop-start">
              {!busy && <Ic icon={IconPlayerPlay} size={14} />}
              {busy ? 'Starting…' : 'Start'}
            </Button>
          )}
        </header>
      )}
      <div className={`desktop-wrap ${fs ? 'is-fullscreen' : ''}`} ref={wrapRef}>
        {fs && (
          <>
            {/* The iframe swallows mouse events, so a thin strip at the top edge reveals the bar. */}
            <div className="fs-hot" onMouseEnter={showBar} data-testid="fs-hot" />
            <div className={`fs-bar ${bar ? 'open' : ''}`} onMouseEnter={showBar} onMouseLeave={hideBarSoon} data-testid="fs-bar">
              <span className="fs-title">Desktop{machineId ? ` — ${machineId}` : ''}</span>
              <span className="spacer" />
              {viewing ? (
                <Button variant="primary" size="sm" onClick={takeControl}>
                  <Ic icon={IconHandClick} size={14} />
                  Take control
                </Button>
              ) : (
                picker('fs-res')
              )}
              <Button size="sm" onClick={fullscreen} data-testid="fs-exit">
                Exit fullscreen
              </Button>
            </div>
          </>
        )}
        <div className="desktop-stage" ref={stageRef} data-actual={showActual ? '' : undefined}>
          {running && driver && !viewing && (
            <div className="ws-driving" role="status" data-testid="desktop-driving">
              <Mark tone="run" ring blink />
              <span className="ws-driving-name">{driver.name} is using the desktop</span>
              <span className="ws-driving-task">{driver.task}</span>
              <button type="button" className="ws-driving-take" onClick={takeControl} data-testid="desktop-take-over">
                <Ic icon={IconHandStop} size={14} />
                Take over
              </button>
            </div>
          )}
          {running ? (
            <>
              {role !== null && (
                <iframe
                  key={`${gen}-${role}`}
                  ref={(el) => {
                    frameRef.current = el;
                    setDesktopFrame(el);
                  }}
                  className={`desktop-frame ${loaded ? 'is-loaded' : ''}`}
                  style={showActual && sizeNow ? { width: sizeNow[0], height: sizeNow[1] } : undefined}
                  title="Desktop"
                  src={src}
                  allow={frameAllow}
                  allowFullScreen
                  data-testid="desktop-frame"
                  data-loaded={loaded ? '1' : '0'}
                  onLoad={() => setLoaded(true)}
                />
              )}
              <LoginOverlay active={loaded && role === 'control'} frame={frameRef.current} />
              {!loaded && (
                <div className="desktop-overlay">
                  <div className="desktop-progress">
                    <span className="loading-label">connecting to desktop</span>
                    <span className="progress-bar" aria-hidden="true" />
                  </div>
                </div>
              )}
            </>
          ) : busy ? (
            <div className="ws-dempty">
              <div className="ws-dempty-col" role="status">
                <p className="ws-dempty-kicker">
                  <Mark tone="run" blink />
                  starting desktop
                </p>
                <span className="progress-bar ws-dempty-bar" aria-hidden="true" />
                <p className="ws-dempty-note">Booting a Linux desktop with Chrome. This takes a few seconds.</p>
              </div>
            </div>
          ) : (
            <div className="ws-dempty">
              <div className="ws-dempty-col">
                <EmptyArt kind="desktop" size="lg" className="ws-dempty-art" />
                <p className="ws-dempty-kicker">
                  <span className="ws-dempty-carets">
                    <Carets />
                  </span>
                  {machineId ? `${machineId} · desktop` : 'desktop'}
                </p>
                <h1 className="ws-dempty-title">Desktop is stopped.</h1>
                <p className="ws-dempty-lead">A streamed Linux desktop with Chrome: watch your agents drive a browser, take the mouse when needed.</p>
                {error && (
                  <p className="ws-dempty-error" role="alert" data-testid="desktop-error">
                    <Ic icon={IconAlertTriangle} size={14} />
                    <span>{desktopProblem(error)}</span>
                  </p>
                )}
                <Button variant="primary" className="ws-dempty-go" onClick={() => void a.startDesktop()} disabled={conn !== 'connected'}>
                  <Ic icon={IconPlayerPlay} size={14} />
                  Start desktop stream
                </Button>
              </div>
            </div>
          )}
        </div>
        {/* The desktop's windows and apps: below the stream, never over it (in fullscreen too). */}
        {running && <DesktopDock live={conn === 'connected'} viewOnly={viewing} sheet={a.compact} />}
      </div>
    </>
  );
}
